import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { Shell } from "@/components/Shell";
import { money, dateLabel } from "@/lib/format";
import { ProgressRing } from "@/components/ProgressRing";
import { Sun, ClipboardText, ChartBar, BellRinging, MoonStars, MapPin } from "@phosphor-icons/react";

export default function Home() {
  const { user, session } = useAuth();
  const { lang, t } = useLang();
  const nav = useNavigate();
  const [dash, setDash] = useState(null);

  useEffect(() => { api.get("/dashboard/mr").then(({ data }) => setDash(data)); }, [session]);

  const today = dash?.today || {};
  const target = dash?.call_target || user?.call_target || 12;
  const done = today.total_visits || 0;
  const pct = target ? Math.min(100, Math.round((done / target) * 100)) : 0;
  const started = !!session;
  const submitted = session?.status === "submitted";

  const actions = [
    { key: "start_my_day", icon: Sun, color: "bg-emerald-600", to: "/start-day", disabled: started },
    { key: "add_visit", icon: ClipboardText, color: "bg-slate-800", to: "/add-visit", disabled: !started || submitted },
    { key: "todays_summary", icon: ChartBar, color: "bg-blue-600", to: "/summary" },
    { key: "pending_followups", icon: BellRinging, color: "bg-amber-500", to: "/followups" },
    { key: "end_my_day", icon: MoonStars, color: "bg-slate-700", to: "/end-day", disabled: !started || submitted },
  ];

  return (
    <Shell title={t("home")}>
      <div className="space-y-4">
        <div className="rounded-2xl bg-slate-800 text-white p-4">
          <p className="text-slate-300 text-xs">{dateLabel(lang)}</p>
          <p className="font-head font-bold text-xl mt-1">{user?.name}</p>
          <p className="text-slate-400 text-sm">{user?.employee_id} · {user?.headquarters}</p>
          <div className="mt-3 flex items-center gap-2 text-xs">
            <MapPin size={14} />
            <span className="text-slate-300">{session?.working_area || user?.working_areas?.[0] || "—"}</span>
            <span className={`ml-auto px-2 py-0.5 rounded-full font-semibold ${submitted ? "bg-blue-500" : started ? "bg-emerald-500" : "bg-amber-500 text-slate-900"}`}>
              {submitted ? t("report_submitted") : started ? t("day_started") : t("day_not_started")}
            </span>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="font-semibold text-slate-700">{t("target_vs_ach")}</span>
            <span className="text-slate-500" data-testid="target-progress">{done} / {target}</span>
          </div>
          <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-400"}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4 text-center">
            <Stat label={t("completed_calls")} value={done} />
            <Stat label={t("order_value")} value={money(today.order_value)} />
            <Stat label={t("collection")} value={money(today.collection)} />
          </div>
        </div>

        {dash?.targets && (
          <div className="rounded-2xl bg-white border border-slate-200 p-4" data-testid="targets-card">
            <div className="text-sm font-semibold text-slate-700 mb-3">{t("daily_targets")}</div>
            <div className="grid grid-cols-3 gap-2">
              <ProgressRing label={t("t_doctors")} value={today.total_doctor_visits || 0} target={dash.targets.daily_doctor} testid="ring-doctors" />
              <ProgressRing label={t("t_retailers")} value={today.retailer_visits || 0} target={dash.targets.daily_retailer} testid="ring-retailers" />
              <ProgressRing label={t("t_stockists")} value={today.stockist_visits || 0} target={dash.targets.daily_stockist} testid="ring-stockists" />
            </div>
            {(dash.targets.monthly_order > 0 || dash.targets.monthly_collection > 0) && (
              <>
                <div className="text-sm font-semibold text-slate-700 mt-4 mb-3">{t("monthly_targets")}</div>
                <div className="grid grid-cols-2 gap-2">
                  <ProgressRing label={t("t_order")} value={dash.month_order_value || 0} target={dash.targets.monthly_order} money testid="ring-order" />
                  <ProgressRing label={t("t_collection")} value={dash.month_collection || 0} target={dash.targets.monthly_collection} money testid="ring-collection" />
                </div>
              </>
            )}
          </div>
        )}

        <div className="space-y-3">
          {actions.map(({ key, icon: Icon, color, to, disabled }) => (
            <button
              key={key}
              data-testid={`home-${key}`}
              disabled={disabled}
              onClick={() => nav(to)}
              className={`w-full min-h-[64px] rounded-xl ${color} text-white flex items-center gap-4 px-5 font-head font-bold text-lg active:scale-[0.98] transition-transform disabled:opacity-40`}
            >
              <Icon size={26} weight="bold" />
              {t(key)}
            </button>
          ))}
        </div>
      </div>
    </Shell>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 py-2">
      <div className="font-bold text-slate-800">{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}
